import { gql } from 'graphql-tag';

export const premiereSchema = gql`
  extend type Query {
    activeProject: PremiereProject
    getPlayheadSyncStatus(assetId: ID!, sequenceId: ID): PlayheadSyncStatus!
    getAllExportsXmpMetadata: [ExportsXMPMetadata]!
    getExportPresets: [PremiereExportPreset]!
    getExportSettings: ExportSettings
    getExportSettingsMetadata(
      input: ExportSettingsMetadataInput!
    ): ExportSettingsMetadata
    getMarkerSyncStatus(assetId: ID!, sequenceId: ID): MarkerSyncStatus!
      @deprecated(
        reason: "Use commentMarkerSyncStatus; for Premiere pass sequence id as documentId."
      )
    """
    Legacy sequence markers for export. Prefer Query.documentMarkers.
    """
    getMarkersFromSequence(sequenceId: ID!): [Marker]!
      @deprecated(reason: "Use documentMarkers; pass sequenceId as documentId.")
  }

  type ExportSettingsMetadata {
    audio: String!
    bitRate: String!
    colorSpace: String!
    duration: Float!
    estimatedFileSizeMB: String!
    frameRate: String!
    resolution: String!
    format: String!
  }

  input ExportSettingsMetadataInput {
    presetId: ID!
    entireSequence: Boolean!
    format: String
  }

  input ExportSettingsInput {
    localCopyEnabled: Boolean
    localFolderPath: String
    shareEnabled: Boolean
    shareDownloadsEnabled: Boolean
    shareCommentsEnabled: Boolean
    assetFolderId: String
    assetFolderName: String
    presetId: String
    range: ExportAssetRange
    exportMarkersAsCommentsEnabled: Boolean
  }

  input ImportAssetsAssetInput {
    id: ID!
    filename: String!
    url: String!
    path: String!
    filesize: Float! # in bytes
  }

  input ImportAssetsInput {
    assets: [ImportAssetsAssetInput!]!
    path: String
  }

  enum PlayheadSyncStatusState {
    ENABLED
    DISABLED
  }

  type PlayheadSyncStatus {
    assetId: ID!
    status: PlayheadSyncStatusState!
    sequenceId: ID!
  }

  input SetPlayheadSyncStatusInput {
    assetId: ID!
    status: PlayheadSyncStatusState!
    sequenceId: ID!
  }

  input SetPlayheadPositionInput {
    assetId: ID!
    positionMicroseconds: Float!
    sequenceId: ID!
  }

  type PlayheadPositionPayload {
    positionMicroseconds: Float!
    sequenceId: ID!
  }

  input CancelExportInput {
    id: ID!
  }

  input CancelImportInput {
    id: ID!
  }

  extend type Mutation {
    updateExportSettings(input: ExportSettingsInput!): ExportSettings

    exportSequence(
      fileName: String!
      presetId: ID!
      fullSequence: Boolean!
      filePath: String
      format: String
    ): ExportSequencePayload!

    importAssets(input: ImportAssetsInput!): ImportAssetsPayload!

    openPresetManager: Boolean

    setPlayheadSyncStatus(
      input: SetPlayheadSyncStatusInput!
    ): PlayheadSyncStatus!

    setPlayheadPosition(input: SetPlayheadPositionInput!): Boolean

    saveExportXmpMetadataToSequence(
      sequenceId: ID!
      includeInOutTimestamps: Boolean!
      xmpMetadata: SequenceXMPMetadataInput!
    ): PremiereSequence

    setSequenceMetadataProperty(
      sequenceId: ID!
      propertyName: String!
      propertyValue: String!
    ): PremiereSequence

    deleteExportMetadataForAsset(
      assetId: ID!
      sequenceId: ID
    ): PremiereSequence @deprecated(reason: "Use removeExportMetadata instead")
    removeExportMetadata(assetId: ID!, sequenceId: ID): [PremiereSequence]!
    deleteV4CommentsPanelMetadataForShare(
      shareId: ID!
      sequenceId: ID
    ): PremiereSequence
    deleteV3PanelExportsForSequence(sequenceId: ID!): PremiereSequence

    cancelExport(input: CancelExportInput!): Boolean
    cancelImport(input: CancelImportInput!): Boolean

    setActiveSequence(sequenceId: ID!): Boolean

    # Set the focus to the Frame.io panel in the Premiere Pro UI
    setPremiereFocusToFrameioPanel: Boolean!

    setMarkerSyncStatus(input: SetMarkerSyncStatusInput!): MarkerSyncStatus!
      @deprecated(reason: "Use setCommentMarkerSyncStatus.")
    setSequencePosition(input: SetSequencePositionInput!): Boolean
      @deprecated(reason: "Use setDocumentPosition.")
  }

  input SequenceXMPMetadataInput {
    assetId: ID!
    versionId: ID
    shareId: ID
    projectId: ID!
    accountId: ID!
  }

  union ExportsXMPMetadata =
      SequenceExportsXMPMetadata
    | V4CommentsPanelExportsXMPMetadata
    | V3PanelExportsXMPMetadata

  # All future fields added here should be nullable since previous exports
  # will not contain the new field data.
  type SequenceExportsXMPMetadata {
    assetId: ID!
    sequenceId: ID
    sequenceName: String
    shareId: ID
    versionId: ID
    projectId: ID!
    accountId: ID!
    exportedAt: Float!
    sequenceIn: Float
    sequenceOut: Float
  }

  type V4CommentsPanelExportsXMPMetadata {
    exportedAt: Float!
    shareId: ID
    sequenceId: ID
  }

  # V3 stored [asset1Id, asset2Id, asset3Id,...] for every export regardless
  # of versioning. If we use this data we'll need to query to determine
  # which asset IDs are versioned together.
  type V3PanelExportsXMPMetadata {
    assetIds: [ID!]!
    sequenceId: ID
  }

  type ImportAssetsPayload {
    id: ID!
    name: String!
  }

  enum ExportMetadataChangeReason {
    LINKED_SEQUENCE_ADDED
    LINKED_SEQUENCE_DELETED
  }

  type ExportMetadataChangedPayload {
    reason: ExportMetadataChangeReason!
  }

  type Subscription {
    exportMetadataEvents: ExportMetadataChangedPayload!
    sequenceEvents: SequenceEventsPayload!
    importEvents: ImportEventsPayload!
    premiereProjectEvents: PremiereProjectEventsPayload!
    playheadPositionChanged: PlayheadPositionPayload!
  }

  type SequenceEncodeProgressPayload {
    id: ID!
    progress: Float
  }

  type SequenceEncodeProgressErrorPayload {
    id: ID!
    message: String!
  }

  type ImportItemError {
    path: String!
    message: String!
  }

  type ImportCompletePayload {
    id: ID!
    paths: [String!]!
    errors: [ImportItemError!]!
  }

  type ImportCanceledPayload {
    id: ID!
  }

  type SequenceEncodeCompletePayload {
    id: ID!
    filePath: String!
  }

  type SequenceEncodeCanceledPayload {
    id: ID!
  }

  type SequenceUploadJobProgressPayload {
    id: ID!
    # This field is present only in older payload versions and has been removed in newer ones.
    # It is retained here to ensure backward compatibility.
    progress: Float
    # The following fields are optional for backwards compatibility
    # They may be absent in earlier versions of the payload
    bytesSent: Float
    bytesTotal: Float
    completedItems: Int
    failedItems: Int
    jobId: ID
    percentage: Float
    state: PremiereTransferState
    totalItems: Int
  }

  type SequenceUploadJobItemProgressPayload {
    id: ID!
    # Optional for backwards compatibility - may not exist in all payload versions
    jobId: ID
    assetId: ID!
    bytesSent: Float
    bytesTotal: Float
    filePath: String!
    itemId: ID!
    percentage: Float
    remainingTime: Float
    state: PremiereTransferState
  }

  type DownloadJobProgressPayload {
    id: ID!
    jobId: ID!
    bytesSent: Float!
    bytesTotal: Float!
    completedItems: Int!
    failedItems: Int!
    percentage: Float!
    state: PremiereTransferState!
    totalItems: Int!
  }

  type SequenceActivatedPayload {
    sequenceId: ID!
    sequenceName: String!
  }

  type SequenceClosedPayload {
    sequenceId: ID!
  }

  union SequenceEventsPayload =
      SequenceActivatedPayload
    | SequenceClosedPayload
    | SequenceEncodeCanceledPayload
    | SequenceEncodeCompletePayload
    | SequenceEncodeProgressPayload
    | SequenceEncodeProgressErrorPayload
    | SequenceUploadJobItemProgressPayload
    | SequenceUploadJobProgressPayload

  union ImportEventsPayload =
      DownloadJobProgressPayload
    | ImportCompletePayload
    | ImportCanceledPayload

  type PremiereProjectClosedPayload {
    projectId: ID
    projectName: String
  }

  type PremiereProjectOpenedPayload {
    projectId: ID
    projectName: String
  }

  union PremiereProjectEventsPayload =
      PremiereProjectOpenedPayload
    | PremiereProjectClosedPayload

  type ExportSequencePayload {
    filePath: String
    batchItemId: ID
  }

  type PremiereProject {
    id: ID
    name: String
    activeSequence: PremiereSequence
  }

  type PremiereSequence {
    id: ID
    name: String
    metadata: [ExportsXMPMetadata]
    end: Float
    inPoint: Float
    outPoint: Float
    start: Float
  }

  type PremiereExportPreset {
    id: ID!
    name: String!
    format: String!
    filePath: String
    presetSummaryJSON: String
    isFavorite: Boolean
    isCustom: Boolean
  }

  enum ExportAssetRange {
    entireSequence
    sequenceInOut
  }

  type ExportSettings {
    localCopyEnabled: Boolean
    localFolderPath: String
    shareEnabled: Boolean
    shareDownloadsEnabled: Boolean
    shareCommentsEnabled: Boolean
    assetFolderId: String
    assetFolderName: String
    presetId: String
    range: ExportAssetRange
    exportMarkersAsCommentsEnabled: Boolean
  }

  # Legacy Premiere Pro sequence-oriented marker APIs. Prefer shared CommentMarker* / Document* fields.
  type Marker {
    id: ID
    comments: String!
    deleted: Boolean!
    duration: Float
    frameCommentId: ID!
    name: String!
    start: Float!
  }

  type MarkerSyncStatus {
    assetId: ID!
    sequenceId: ID
    status: MarkerSyncStatusState!
  }

  input SetMarkerSyncStatusInput {
    assetId: ID!
    status: MarkerSyncStatusState!
    sequenceId: ID
  }

  input SetSequencePositionInput {
    assetId: ID!
    commentId: ID
    timestampMicroseconds: Float!
  }

  enum PremiereTransferState {
    pending
    failed
    completed
    uploading
    downloading
    canceled
    partially_completed
  }
`;
